import { useState, useMemo, useRef, useEffect } from "react";
import { VOICES } from "../lib/api";
import { KEY_MIN, KEY_MAX, isBlackKey, notePt, noteLetter, octaveOf, midiFromIntl, playNote, preloadNotes, noteOn, noteOff, VOICE_SHORT } from "../lib/piano";
import { Icon } from "./icons";

const WHITE_W = 40;
const BLACK_W = 26;

/** "Piano" section of the song page: hear your section's entry note, the others', and play freely. */
export function Piano({ startNotes = {}, myVoice }) {
  const [open, setOpen] = useState(false);
  const kbRef = useRef(null);
  // On small screens, bring the member's starting note into view.
  useEffect(() => {
    if (!open) return;
    const el = kbRef.current?.querySelector('[data-voice-key="mine"]') || kbRef.current?.querySelector("[data-voice-key]");
    el?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [open]);
  const myMidi = myVoice ? midiFromIntl(startNotes[myVoice]) : null;
  const voicesWithNotes = VOICES.filter((v) => midiFromIntl(startNotes[v]) != null);

  // midi -> [voice letters] for keyboard highlighting
  const voiceByMidi = useMemo(() => {
    const map = {};
    VOICES.forEach((v) => {
      const m = midiFromIntl(startNotes[v]);
      if (m != null) (map[m] = map[m] || []).push(v);
    });
    return map;
  }, [startNotes]);

  const toggle = () => {
    if (!open) {
      const center = myMidi ?? 60;
      preloadNotes([...voicesWithNotes.map((v) => midiFromIntl(startNotes[v])), ...Array.from({ length: 25 }, (_, i) => center - 12 + i)]);
    }
    setOpen((o) => !o);
  };
  const playChord = () => voicesWithNotes.forEach((v, i) => setTimeout(() => playNote(midiFromIntl(startNotes[v])), i * 40));

  const keys = Array.from({ length: KEY_MAX - KEY_MIN + 1 }, (_, i) => KEY_MIN + i);
  const whites = keys.filter((k) => !isBlackKey(k));
  const whiteIndex = {};
  whites.forEach((k, i) => (whiteIndex[k] = i));
  const blacks = keys.filter((k) => isBlackKey(k));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="song-piano">
      <button onClick={toggle} data-testid="song-piano-toggle" style={{ display: "flex", alignItems: "center", gap: 12, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-md)", padding: 14, cursor: "pointer", textAlign: "left", color: "inherit" }}>
        <span style={{ width: 36, height: 36, borderRadius: 999, background: "var(--brand-tertiary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name="musical-notes" size={18} color="var(--brand-primary)" />
        </span>
        <span style={{ flex: 1 }}>
          <div style={{ fontFamily: "var(--f-medium)", fontSize: 11, letterSpacing: 2 }}>PIANO</div>
          <div className="t-hint">{myMidi != null ? `A sua nota de entrada: ${notePt(myMidi)}` : "Confirme as notas no teclado"}</div>
        </span>
        <Icon name={open ? "chevron-up" : "chevron-down"} size={18} color="var(--muted)" />
      </button>

      {open ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {myMidi != null ? (
            <button onClick={() => playNote(myMidi)} data-testid="song-piano-my-note" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "var(--brand-primary)", color: "var(--on-brand)", border: "none", borderRadius: "var(--r-pill)", height: 50, cursor: "pointer", fontFamily: "var(--f-medium)", letterSpacing: 1 }}>
              <Icon name="play" size={18} color="var(--on-brand)" /> OUVIR A MINHA NOTA · {notePt(myMidi).toUpperCase()}
            </button>
          ) : null}

          {voicesWithNotes.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {voicesWithNotes.map((v) => {
                const m = midiFromIntl(startNotes[v]);
                const mine = v === myVoice;
                return (
                  <button key={v} onClick={() => playNote(m)} data-testid={`song-piano-voice-${v.toLowerCase()}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 74, padding: "8px 12px", borderRadius: "var(--r-md)", cursor: "pointer", border: `1px solid ${mine ? "var(--brand-primary)" : "var(--border)"}`, background: mine ? "var(--brand-tertiary)" : "var(--surface-2)", color: "inherit" }}>
                    <span style={{ fontFamily: "var(--f-medium)", fontSize: 11, letterSpacing: 1 }}>{v.toUpperCase()}</span>
                    <span className="t-hint">{notePt(m)}</span>
                  </button>
                );
              })}
              {voicesWithNotes.length > 1 ? (
                <button onClick={playChord} data-testid="song-piano-chord" style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: "var(--r-md)", cursor: "pointer", border: "1px solid var(--border-strong)", background: "var(--surface-3)", color: "inherit", fontFamily: "var(--f-medium)", fontSize: 11, letterSpacing: 1 }}>
                  <Icon name="list" size={14} /> TODAS
                </button>
              ) : null}
            </div>
          ) : (
            <div className="t-hint">Ainda não há notas de entrada registadas para esta música.</div>
          )}

          <Keyboard kbRef={kbRef} whites={whites} blacks={blacks} whiteIndex={whiteIndex} voiceByMidi={voiceByMidi} myVoice={myVoice} />
          <div className="t-hint">C2 a C5 · deslize para ver todas as teclas · mantenha a tecla premida para sustentar · várias teclas em simultâneo.</div>
        </div>
      ) : null}
    </div>
  );
}

/** Multitouch keyboard: a note starts on press, sustains while held and is released (short damper) on lift/cancel. */
function Keyboard({ kbRef, whites, blacks, whiteIndex, voiceByMidi, myVoice }) {
  const held = useRef(new Map()); // pointerId -> { midi, h }
  const [down, setDown] = useState(() => new Set());
  const loadedAll = useRef(false);
  const openedAt = useRef(Date.now());
  const sync = () => setDown(new Set([...held.current.values()].map((x) => x.midi)));
  const release = (id) => { const x = held.current.get(id); if (!x) return; noteOff(x.h); held.current.delete(id); sync(); };
  const releaseAll = () => { [...held.current.keys()].forEach(release); };
  useEffect(() => {
    const onHide = () => releaseAll();
    window.addEventListener("blur", onHide);
    document.addEventListener("visibilitychange", onHide);
    return () => { window.removeEventListener("blur", onHide); document.removeEventListener("visibilitychange", onHide); releaseAll(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const press = (midi) => (e) => {
    if (e.button && e.button !== 0) return;
    release(e.pointerId);
    held.current.set(e.pointerId, { midi, h: noteOn(midi) });
    sync();
  };
  const lift = (e) => release(e.pointerId);
  const keyProps = (midi) => ({
    onPointerDown: press(midi), onPointerUp: lift, onPointerCancel: lift, onPointerLeave: lift, onLostPointerCapture: lift,
    onContextMenu: (e) => e.preventDefault(),
    onKeyDown: (e) => { if ((e.key === " " || e.key === "Enter") && !e.repeat) { e.preventDefault(); press(midi)({ pointerId: `k${midi}` }); } },
    onKeyUp: (e) => { if (e.key === " " || e.key === "Enter") release(`k${midi}`); },
    "data-testid": `piano-key-${midi}`, "aria-label": `Nota ${noteLetter(midi)}${octaveOf(midi)}`,
  });
  // Lazy-load the remaining samples only when the member explores the keyboard.
  const onScroll = () => { if (!loadedAll.current && Date.now() - openedAt.current > 800) { loadedAll.current = true; preloadNotes([...whites, ...blacks]); } };
  return (
    <div ref={kbRef} className="piano-kb" onScroll={onScroll} style={{ overflowX: "auto", paddingBottom: 6, WebkitOverflowScrolling: "touch" }} data-testid="piano-keyboard">
      <div style={{ position: "relative", height: 160, width: whites.length * WHITE_W }}>
        {whites.map((midi) => {
          const vs = voiceByMidi[midi];
          const mine = myVoice && vs?.includes(myVoice);
          return (
            <button key={midi} {...keyProps(midi)} className={down.has(midi) ? "down" : ""} data-voice-key={vs ? (mine ? "mine" : "other") : undefined}
              style={{ position: "absolute", left: whiteIndex[midi] * WHITE_W, top: 0, width: WHITE_W - 2, height: 160, borderRadius: "0 0 6px 6px", border: "1px solid #000", background: vs ? (mine ? "var(--brand-primary)" : "var(--brand-tertiary)") : "#F3F3EE", color: "#111", cursor: "pointer", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 2, paddingBottom: 6 }}>
              {vs ? <span style={{ fontSize: 10, fontFamily: "var(--f-medium)", color: mine ? "var(--on-brand)" : "var(--on-surface)" }}>{vs.map((v) => VOICE_SHORT[v]).join("")}</span> : null}
              <span style={{ fontSize: 11, fontFamily: "var(--f-medium)", color: mine ? "var(--on-brand)" : "#333", pointerEvents: "none" }}>{noteLetter(midi)}{midi % 12 === 0 ? <sub style={{ fontSize: 8 }}>{octaveOf(midi)}</sub> : null}</span>
            </button>
          );
        })}
        {blacks.map((midi) => {
          const leftWhite = whiteIndex[midi - 1];
          const vs = voiceByMidi[midi];
          const mine = myVoice && vs?.includes(myVoice);
          return (
            <button key={midi} {...keyProps(midi)} className={down.has(midi) ? "down" : ""} data-voice-key={vs ? (mine ? "mine" : "other") : undefined}
              style={{ position: "absolute", left: (leftWhite + 1) * WHITE_W - BLACK_W / 2 - 1, top: 0, width: BLACK_W, height: 100, borderRadius: "0 0 5px 5px", border: "1px solid #000", background: vs ? (mine ? "var(--brand-primary)" : "var(--brand-secondary)") : "#1A1A1A", color: "#EEE", cursor: "pointer", zIndex: 2, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 1, paddingBottom: 4 }}>
              {vs ? <span style={{ fontSize: 9, fontFamily: "var(--f-medium)" }}>{vs.map((v) => VOICE_SHORT[v]).join("")}</span> : null}
              <span style={{ fontSize: 8, fontFamily: "var(--f-medium)", pointerEvents: "none" }}>{noteLetter(midi)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
